import { memo } from 'react';
import { Heading as RACHeading, HeadingProps as RACHeadingProps } from 'react-aria-components';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';

export type HeadingProps = Omit<RACHeadingProps, 'level'> &
  Partial<ComponentContextProps> & {
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | null;
  };

const IHeading = ({ variant, size, className, children, ...rest }: HeadingProps) => {
  const context = useComponent({ variant });

  let level;
  let sizeStyle;
  switch (size) {
    case 'xs':
      level = 5;
      sizeStyle = 'text-xs font-regular';
      break;
    case 'sm':
      level = 4;
      sizeStyle = 'text-sm';
      break;
    /* default size is "md" */
    case undefined:
    case null:
    case 'md':
      level = 3;
      sizeStyle = 'text-md';
      break;
    case 'lg':
      level = 2;
      sizeStyle = 'text-lg';
      break;
    case 'xl':
      level = 1;
      sizeStyle = 'text-xl';
      break;
  }

  /* prettier-ignore */
  const styles = [
    'font-inter',
    'font-semibold',
    sizeStyle,

    /* Button */
    'group-[*]/button:font-semibold',

    /* FilterGroup */
    'group-[*]/filter-group:font-medium',
    'group-data-[selected]/filter-group:font-semibold',

    /* MenuItem */
    'group-[*]/menu-item:font-medium',
    'group-data-[selected]/menu-item:font-semibold',
    
    /* Card */
    'group-[*]/card:font-medium',
  ];

  /* prettier-ignore */
  const plainStyles = [
    'text-gray-700',

     /* Button */
    'group-[*]/button:text-gray-600',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-700',
    'group-hover/button:text-gray-700',

    /* MenuItem */
    'group-[*]/menu-item:text-gray-600',
    'group-data-[selected]/menu-item:text-gray-800',

    /* FilterGroup */
    'group-data-[selected]/filter-group:text-primary-600',

    /* ModelPanel */
    'group-[*]/model-panel:text-gray-500',
    'group-[*]/model-panel:font-medium',
    'group-[*]/model-panel:text-sm',

    /* TutorialBox */
    'group-[*]/tutorial-box:font-semibold',

    /* Tab */
    'group-[*]/tab:text-gray-500',
    'group-data-[selected]/tab:text-gray-700',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    'text-primary-900',

    /* Button */
    'group-[*]/button:text-white',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-white',
    'group-hover/button:text-white',

    /* Tooltip */
    'group-[*]/tooltip:text-gray-200',
  ];

  /* prettier-ignore */
  const secondaryStyles = [
    'text-gray-900',

    /* Button */
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-800',
    'group-hover/button:text-gray-800',
  ];

  /* prettier-ignore */
  const tertiaryStyles = [
    'text-white',

    /* Button */
    'group-data-[disabled]/button:text-white',
    'group-data-[pressed]/button:text-white',
    'group-hover/button:text-gray-100',
  ];

  /* prettier-ignore */
  const successStyles = [
    'text-success-900',

    /* Button */
    'group-data-[disabled]/button:text-success-500',
    'group-data-[pressed]/button:text-success-700',
    'group-hover/button:text-success-700',
  ];

  /* prettier-ignore */
  const warningStyles = [
    'text-warning-900',

    /* Button */
    'group-data-[disabled]/button:text-warning-500',
    'group-data-[pressed]/button:text-warning-700',
    'group-hover/button:text-warning-700',
  ];

  /* prettier-ignore */
  const errorStyles = [
    'text-error-700',

    /* Button */
    'group-data-[disabled]/button:text-error-500',
    'group-data-[pressed]/button:text-error-700',
    'group-hover/button:text-error-700',
  ];

  return (
    <RACHeading
      className={merge(
        styles,
        context.variant === 'default' && plainStyles,
        context.variant === 'plain' && plainStyles,
        context.variant === 'primary' && primaryStyles,
        context.variant === 'secondary' && secondaryStyles,
        context.variant === 'tertiary' && tertiaryStyles,
        context.variant === 'success' && successStyles,
        context.variant === 'warning' && warningStyles,
        context.variant === 'error' && errorStyles,
        className
      )}
      level={level}
      {...rest}
    >
      {renderWithContext(children, context)}
    </RACHeading>
  );
};

export const Heading = memo(IHeading) as typeof IHeading;
